/* ═══════════════════════════════════════════════════════════════════════
   "What crAIg knows" — changing it without redoing the interview.

   The setup conversation already asks all of this and produces exactly
   this config, so this screen is NOT a second way to set it up. It is the
   answer to a question a business has on day two and currently cannot
   answer at all: what does crAIg currently think our call-out fee is?

   ── Which is why it shows before it edits ──────────────────────────────
   Every panel here is a list of what he already believes, in the
   business's own words, with the edit in place. Nobody arrives wanting to
   "configure eligibility rules"; they arrive because a price changed, or
   because he told somebody something wrong and they want to find it and
   fix it.

   ── Same validator as the conversation ─────────────────────────────────
   Saved through `validateOverlay`, which is what the interview and a
   hand-written config both go through. A form gets no easier ride than
   crAIg does — otherwise this becomes the way to write a config the engine
   chokes on.

   ── It edits a SUBSET, and must not eat the rest ───────────────────────
   The overlay also holds their page, their assistant and their modules,
   owned by other screens. This loads the whole config and writes the whole
   config back, patching only its own keys, so saving here can never
   silently delete a page somebody wrote yesterday.
   ═══════════════════════════════════════════════════════════════════════ */

import { useEffect, useState } from "react";
import { applyAccent, applyTheme, prefersDark, type Theme } from "./theme";
import { CraigFace } from "./components/CraigFace";
import { DashboardLink } from "./components/chrome";
import { LetCraigDoIt, Wordmark } from "./components/Brand";

interface Knowledge {
  id: string;
  question: string;
  answer: string;
}

interface Rule {
  id: string;
  field: string;
  oneOf?: string[];
  noneOf?: string[];
  reason: string;
  services?: string[];
}

interface Config {
  knowledge?: Knowledge[];
  eligibility?: Rule[];
  houseRules?: string;
  services?: { only?: string[] };
  [k: string]: unknown;
}

const slug = (s: string) =>
  s.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-|-$/g, "").slice(0, 40) || `x${Date.now() % 100000}`;

export function SetupEditor({ page }: { page: string }) {
  /* Which page's setup this is. */

  const [theme, setTheme] = useState<Theme>(() => (prefersDark() ? "dark" : "light"));
  const [config, setConfig] = useState<Config>({});
  const [services, setServices] = useState<{ id: string; label: string }[]>([]);
  const [fields, setFields] = useState<{ name: string; label: string }[]>([]);
  const [packName, setPackName] = useState("");
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    applyTheme(theme);
    applyAccent(undefined, theme);
  }, [theme]);

  useEffect(() => {
    fetch(`/api/dash/${page}/config`)
      .then((r) => (r.ok ? r.json() : Promise.reject(r.status)))
      .then((c) => {
        setConfig(c.config ?? {});
        setServices(c.availableServices ?? []);
        setFields(c.availableFields ?? []);
        setPackName(c.packName ?? "");
      })
      .catch((s) => {
        if (s === 401) window.location.href = "/account/sign-in";
        else setError("Couldn't load that just now.");
      })
      .finally(() => setLoading(false));
  }, []);

  const patch = (p: Partial<Config>) => setConfig((c) => ({ ...c, ...p }));

  const save = async () => {
    setSaving(true);
    setError(null);
    setSaved(false);
    try {
      /* Tidied on the way out rather than nagged about on the way in:
         a half-typed row someone abandoned should not be an error message,
         it should just not be saved. */
      const clean: Config = {
        ...config,
        knowledge: (config.knowledge ?? []).filter((k) => k.question.trim() && k.answer.trim()),
        eligibility: (config.eligibility ?? []).filter((r) => r.field && r.reason.trim()),
      };
      if (!clean.knowledge?.length) delete clean.knowledge;
      if (!clean.eligibility?.length) delete clean.eligibility;
      if (!clean.houseRules?.trim()) delete clean.houseRules;

      const res = await fetch(`/api/dash/${page}/config`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ config: clean }),
      });
      if (!res.ok) {
        const b = (await res.json().catch(() => ({}))) as { error?: string };
        throw new Error(b.error ?? "That didn't save.");
      }
      setConfig(clean);
      setSaved(true);
      setTimeout(() => setSaved(false), 2500);
    } catch (e) {
      setError(e instanceof Error ? e.message : "That didn't save.");
    } finally {
      setSaving(false);
    }
  };

  if (loading) return <Centre>Loading…</Centre>;

  const knowledge = config.knowledge ?? [];
  const rules = config.eligibility ?? [];
  const only = config.services?.only ?? [];

  return (
    <div className="min-h-screen flex flex-col">
      <header style={{ borderBottom: "1px solid var(--line)", background: "var(--surface)" }}>
        <div className="max-w-3xl mx-auto px-5 py-3.5 flex items-center justify-between gap-3">
          <div className="flex items-center gap-2 shrink-0">
            <a href={`/dashboard/${page}`} className="btn btn-quiet btn-term" style={{ padding: "0.35rem 0.6rem" }}>
              ← Back
            </a>
            <DashboardLink />
          </div>
          <span className="mono truncate" style={{ fontWeight: 650, fontSize: 14.5 }}>
            What crAIg knows
          </span>
          <button
            type="button"
            className="btn btn-quiet btn-term"
            onClick={() => setTheme((t) => (t === "dark" ? "light" : "dark"))}
            aria-label="Switch theme"
            style={{ padding: "0.3rem 0.55rem" }}
          >
            {theme === "dark" ? "☀" : "☾"}
          </button>
        </div>
      </header>

      <main className="flex-1 w-full max-w-3xl mx-auto px-5 py-6 space-y-4">
        {error && (
          <p
            role="alert"
            className="card px-4 py-3"
            style={{ fontSize: 14, color: "var(--danger)", background: "var(--warn-tint)", borderColor: "var(--warn)" }}
          >
            {error}
          </p>
        )}

        <p style={{ fontSize: 14, color: "var(--ink-2)", lineHeight: 1.55 }}>
          This is what crAIg has been told, from your setup conversation. Change anything that's
          out of date — it takes effect on the next customer, and nothing already in progress is
          affected.
        </p>

        {/* ── Services ───────────────────────────────────────────────── */}
        {services.length > 0 && (
          <Panel title="What you do" hint={`From ${packName}. Untick anything you don't take on.`}>
            <div className="flex flex-wrap gap-2">
              {services.map((s) => {
                // Empty means "all of them", which is what a pack does by
                // default — so an empty list must read as everything ticked.
                const on = only.length === 0 || only.includes(s.id);
                return (
                  <button
                    key={s.id}
                    type="button"
                    aria-pressed={on}
                    className={on ? "btn btn-primary btn-term" : "btn btn-quiet btn-term"}
                    onClick={() => {
                      const current = only.length === 0 ? services.map((x) => x.id) : only;
                      const next = on ? current.filter((x) => x !== s.id) : [...current, s.id];
                      patch({
                        services:
                          next.length === services.length ? undefined : { only: next },
                      });
                    }}
                  >
                    {s.label}
                  </button>
                );
              })}
            </div>
          </Panel>
        )}

        {/* ── Knowledge ──────────────────────────────────────────────── */}
        <Panel
          title="What he answers"
          hint="When a customer asks one of these, crAIg says your answer. Anything not here, he says he'll check with you rather than guessing."
        >
          <div className="space-y-2.5">
            {knowledge.map((k, i) => (
              <div key={i} className="panel px-3.5 py-3">
                <div className="flex items-start gap-2">
                  <div className="flex-1 min-w-0 space-y-1.5">
                    <input
                      className="field"
                      placeholder="What do you charge to come out?"
                      value={k.question}
                      onChange={(e) =>
                        patch({
                          knowledge: knowledge.map((x, j) =>
                            i === j ? { ...x, question: e.target.value } : x,
                          ),
                        })
                      }
                    />
                    <textarea
                      className="field"
                      rows={2}
                      placeholder="Nothing — we quote for free."
                      value={k.answer}
                      onChange={(e) =>
                        patch({
                          knowledge: knowledge.map((x, j) =>
                            i === j ? { ...x, answer: e.target.value } : x,
                          ),
                        })
                      }
                    />
                  </div>
                  <Tiny label="Remove" onClick={() => patch({ knowledge: knowledge.filter((_, j) => j !== i) })}>
                    ✕
                  </Tiny>
                </div>
              </div>
            ))}
          </div>
          <button
            type="button"
            className="btn btn-quiet btn-term mt-2.5"
            onClick={() =>
              patch({
                knowledge: [...knowledge, { id: `k${Date.now() % 1000000}`, question: "", answer: "" }],
              })
            }
          >
            + Add an answer
          </button>
        </Panel>

        {/* ── Eligibility ────────────────────────────────────────────── */}
        <Panel
          title="Who you can't help"
          hint="crAIg checks these himself, from the rules — never by judgement. Someone outside is told kindly and early, before they've spent ten minutes on it."
        >
          <div className="space-y-2.5">
            {rules.map((r, i) => {
              const set = (p: Partial<Rule>) =>
                patch({ eligibility: rules.map((x, j) => (i === j ? { ...x, ...p } : x)) });
              const list = r.oneOf ?? r.noneOf ?? [];
              const inclusive = Boolean(r.oneOf);
              return (
                <div key={i} className="panel px-3.5 py-3">
                  <div className="flex items-start gap-2">
                    <div className="flex-1 min-w-0 space-y-2">
                      <div className="flex flex-wrap items-center gap-2" style={{ fontSize: 13.5 }}>
                        <span style={{ color: "var(--ink-3)" }}>Take the job when</span>
                        <select
                          className="field"
                          style={{ width: "auto", padding: "0.3rem 0.5rem" }}
                          value={r.field}
                          onChange={(e) => set({ field: e.target.value })}
                        >
                          {fields.map((f) => (
                            <option key={f.name} value={f.name}>
                              {f.label}
                            </option>
                          ))}
                        </select>
                        <select
                          className="field"
                          style={{ width: "auto", padding: "0.3rem 0.5rem" }}
                          value={inclusive ? "is" : "isn't"}
                          onChange={(e) =>
                            set(
                              e.target.value === "is"
                                ? { oneOf: list, noneOf: undefined }
                                : { noneOf: list, oneOf: undefined },
                            )
                          }
                        >
                          <option value="is">is one of</option>
                          <option value="isn't">is not one of</option>
                        </select>
                      </div>
                      <input
                        className="field"
                        placeholder="6027, 6028, 6030"
                        value={list.join(", ")}
                        onChange={(e) => {
                          const next = e.target.value
                            .split(",")
                            .map((x) => x.trim())
                            .filter(Boolean);
                          set(inclusive ? { oneOf: next } : { noneOf: next });
                        }}
                      />
                      <textarea
                        className="field"
                        rows={2}
                        placeholder="We only cover the northern suburbs — try a plumber closer to you."
                        value={r.reason}
                        onChange={(e) => set({ reason: e.target.value })}
                      />
                      <p style={{ fontSize: 12, color: "var(--ink-3)", lineHeight: 1.45 }}>
                        Said to anyone this turns away. Make it kind and final — they can't fix it.
                      </p>
                    </div>
                    <Tiny label="Remove" onClick={() => patch({ eligibility: rules.filter((_, j) => j !== i) })}>
                      ✕
                    </Tiny>
                  </div>
                </div>
              );
            })}
          </div>
          <button
            type="button"
            className="btn btn-quiet btn-term mt-2.5"
            onClick={() =>
              patch({
                eligibility: [
                  ...rules,
                  {
                    id: slug(`rule-${rules.length + 1}`),
                    field: fields[0]?.name ?? "",
                    oneOf: [],
                    reason: "",
                  },
                ],
              })
            }
          >
            + Add a rule
          </button>
        </Panel>

        {/* ── House rules ────────────────────────────────────────────── */}
        <Panel
          title="How you like things done"
          hint="Anything about tone, your policies, or how you want customers spoken to. He follows it where it's his to follow."
        >
          <textarea
            className="field"
            rows={5}
            maxLength={4000}
            placeholder="Always mention we're licensed and insured. Never promise a same-day slot — the office books those."
            value={config.houseRules ?? ""}
            onChange={(e) => patch({ houseRules: e.target.value })}
          />
          <p style={{ fontSize: 12, color: "var(--ink-3)", marginTop: 6, lineHeight: 1.45 }}>
            Guidance, not instructions. It can't change who he is, loosen anything about private
            details or advice, or ask him to mislead anyone — he'll ignore that part and carry on.
          </p>
        </Panel>

        <div
          className="flex items-center gap-3 flex-wrap"
          style={{ position: "sticky", bottom: 0, background: "var(--bg)", padding: "12px 0" }}
        >
          <button type="button" className="btn btn-primary btn-term" onClick={save} disabled={saving}>
            {saving ? "Saving…" : "Save"}
          </button>
          <a href={`/dashboard/${page}/page`} className="btn btn-quiet btn-term">
            Your page and colours
          </a>
          {saved && <span style={{ fontSize: 13.5, color: "var(--ok)" }}>Saved.</span>}
        </div>
      </main>

      <LetCraigDoIt />
    </div>
  );
}

/* ── Furniture ──────────────────────────────────────────────────────────── */

function Panel({ title, hint, children }: { title: string; hint?: string; children: React.ReactNode }) {
  return (
    <section className="panel panel-ticks px-5 py-5">
      <div className="term-label">{title}</div>
      {hint && (
        <p style={{ fontSize: 13, color: "var(--ink-3)", margin: "4px 0 12px", lineHeight: 1.5 }}>
          {hint}
        </p>
      )}
      {children}
    </section>
  );
}

function Tiny({
  children,
  onClick,
  label,
}: {
  children: React.ReactNode;
  onClick(): void;
  label: string;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      aria-label={label}
      title={label}
      className="btn btn-quiet"
      style={{ padding: "0.2rem 0.5rem", fontSize: 12, flexShrink: 0 }}
    >
      {children}
    </button>
  );
}

function Centre({ children }: { children: React.ReactNode }) {
  return (
    <div className="min-h-screen grid place-items-center px-5">
      <div className="flex flex-col items-center gap-3">
        <CraigFace size={40} thinking />
        <span style={{ fontSize: 14, color: "var(--ink-3)" }}>{children}</span>
        <Wordmark size={12} muted />
      </div>
    </div>
  );
}
